import {
  ragResultToolOperator,
  type RagResultToolOperator,
} from '@kanbu/schema/contracts';
import { ThreadMode } from '@kanbu/schema/enums';
import { Trans } from '@lingui/react/macro';
import { useCallback, useState } from 'react';
import type { PartialDeep } from 'type-fest';

import { OperatorInfo } from './components/operator-info';
import { Message } from '../../components';
import { Button, Markdown } from '../../ui';
import type { ToolComponentProps } from '../tool-provider';
import { createToolComponent } from '../tool-registry';

export interface OperatorResultToolProps
  extends ToolComponentProps<PartialDeep<RagResultToolOperator>> {
  onTakeover?: (accepted: boolean) => Promise<void>;
  mode?: ThreadMode;
}

const hasDisplayText = (text?: string | null): text is string => {
  if (!text) return false;
  const normalizedText = text.trim().toLowerCase();

  return (
    !!normalizedText &&
    normalizedText.length > 0 &&
    normalizedText !== 'null' &&
    normalizedText !== 'undefined'
  );
};

export const OperatorResultTool = createToolComponent<
  OperatorResultToolProps,
  typeof ragResultToolOperator
>(
  {
    schema: {
      args: ragResultToolOperator,
    },
  },
  ({ toolCall, onTakeover, mode }) => {
    const { operator, content } = toolCall.args;

    const [isLoading, setIsLoading] = useState(false);

    const handleTakeover = useCallback(async () => {
      try {
        if (!onTakeover) {
          return;
        }

        setIsLoading(true);
        await onTakeover(mode === ThreadMode.AI);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    }, [mode, onTakeover]);

    return (
      <>
        <Message.BubbleGroup>
          <Message.Bubble>
            <Markdown>{content ?? ''}</Markdown>
          </Message.Bubble>
          <OperatorInfo operator={operator} />
          {hasDisplayText(operator?.displayText) && (
            <Message.Bubble>
              <Markdown>{operator.displayText}</Markdown>
            </Message.Bubble>
          )}
        </Message.BubbleGroup>
        {onTakeover && (
          <div className='flex justify-start gap-1'>
            <Button
              loading={isLoading}
              disabled={isLoading}
              size='lg'
              variant={mode === ThreadMode.Human ? 'outline' : 'primary'}
              onClick={handleTakeover}
              className='rounded-full'
            >
              {mode === ThreadMode.Human ? (
                <Trans>Go back to AI</Trans>
              ) : (
                <Trans>Yes, connect me</Trans>
              )}
            </Button>
          </div>
        )}
      </>
    );
  },
);
